/**
 * V3 — Layout principal (sidebar + topbar). Wrap toutes les pages auth.
 */
import React from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { useV3Auth } from './auth';
import { authLogout } from './api';
import { LayoutDashboard, BookOpen, Wallet, FileText, Users, Receipt, LogOut, ExternalLink, Building2, Truck, Briefcase, ShieldAlert, CreditCard, GitCompareArrows, BarChart3, HardDrive } from 'lucide-react';

export default function V3Layout() {
  const { user, loading, setUser } = useV3Auth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="v3-login-shell">
        <div className="v3-loading-spinner"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/v3/login" replace />;
  }

  const handleLogout = async () => {
    try { await authLogout(); } catch (_e) { /* ignore */ }
    setUser(null);
    navigate('/v3/login', { replace: true });
  };

  return (
    <div className="v3-shell">
      <aside className="v3-sidebar" data-testid="v3-sidebar">
        <div className="v3-sidebar-brand">
          <div className="v3-logo-mark-sm">RO</div>
          <div>
            <div className="v3-brand-title">GROUPE ROPS</div>
            <div className="v3-brand-sub">Direction · v3</div>
          </div>
        </div>

        <nav className="v3-nav">
          <NavLink to="/v3/dashboard" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-dashboard">
            <LayoutDashboard size={18} />
            <span>Dashboard ROPS</span>
          </NavLink>
          <NavLink to="/v3/groupe" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-groupe">
            <Building2 size={18} />
            <span>Cockpit Groupe</span>
          </NavLink>
          <NavLink to="/v3/journal" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-journal">
            <BookOpen size={18} />
            <span>Journal ROPS</span>
          </NavLink>
          <NavLink to="/v3/caisse" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-caisse">
            <Wallet size={18} />
            <span>Caisse</span>
          </NavLink>
          <NavLink to="/v3/sage" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-sage">
            <FileText size={18} />
            <span>Comptabilité</span>
          </NavLink>
          <NavLink to="/v3/livraisons" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-livraisons">
            <Truck size={18} />
            <span>Bons de livraison</span>
          </NavLink>
          <NavLink to="/v3/devis" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-devis">
            <Briefcase size={18} />
            <span>Devis</span>
          </NavLink>
          <NavLink to="/v3/factures" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-factures">
            <CreditCard size={18} />
            <span>Factures &amp; Paiements</span>
          </NavLink>
          <NavLink to="/v3/bank-reconciliation" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-bank-recon">
            <GitCompareArrows size={18} />
            <span>Rapprochement bancaire</span>
          </NavLink>
          <NavLink to="/v3/entity-dashboard" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-entity-dashboard">
            <BarChart3 size={18} />
            <span>Dashboard par entité</span>
          </NavLink>
          <NavLink to="/v3/expenses" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-expenses">
            <Receipt size={18} />
            <span>Dépenses</span>
          </NavLink>
          <NavLink to="/v3/employees" className={({isActive}) => `v3-nav-item ${isActive?'is-active':''}`} data-testid="nav-employees">
            <Users size={18} />
            <span>Employés</span>
          </NavLink>
          {user.role === 'admin' && (
            <NavLink to="/v3/storage-health" className={({isActive}) => `v3-nav-item v3-nav-admin ${isActive?'is-active':''}`} data-testid="nav-storage-health">
              <HardDrive size={18} />
              <span>Santé du stockage</span>
            </NavLink>
          )}
          {user.role === 'admin' && (
            <NavLink to="/v3/audit" className={({isActive}) => `v3-nav-item v3-nav-admin ${isActive?'is-active':''}`} data-testid="nav-audit">
              <ShieldAlert size={18} />
              <span>Audit & Sécurité</span>
            </NavLink>
          )}
        </nav>

        <div className="v3-sidebar-bottom">
          <a href="/legacy-v2.html" className="v3-nav-item v3-nav-external" data-testid="link-legacy">
            <ExternalLink size={16} />
            <span>App legacy</span>
          </a>
        </div>
      </aside>

      <div className="v3-content">
        <header className="v3-topbar">
          <div className="v3-topbar-meta">
            <span className="v3-pill v3-pill-live">● Lecture seule</span>
            <span className="v3-pill v3-pill-domain">grouper-ops.com/v3</span>
          </div>
          <div className="v3-user">
            {user.picture && <img src={user.picture} alt="" className="v3-avatar" />}
            <div className="v3-user-info">
              <div className="v3-user-name">{user.name}</div>
              <div className="v3-user-role">{user.role}</div>
            </div>
            <button onClick={handleLogout} className="v3-logout-btn" data-testid="logout-btn" title="Déconnexion">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <main className="v3-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
